import { BehaviorSubject, Observable, of } from 'rxjs'
import { catchError, tap } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

import { HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'

import { EndpointOptions, RequestService } from './request.service'
import { KartoriumRole, SeatClass } from './permission.service'

export interface OrganizationListItem {
    id: string
    name: string
    display_name: string
    metadata?: {
        customer_id?: string
        type?: string
        [key: string]: any
    }
    branding?: {
        logo_url?: string
        colors?: {
            primary?: string
            page_background?: string
        }
    }
}


export interface KartoriumRolePatch {
    name?: string
    description?: string | null
    seat_class?: SeatClass | null
    visible_modules?: string[] | null
}

export interface RoleAssignment {
    user_id: string
    kartorium_role_id: string
    organization_id: string
}

export interface OrganizationSettings {
    organization_id: string
    hidden_modules: string[]
    default_home_page: string
    created_at?: string
    updated_at?: string
}

export interface OeePageFlags {
    organization_id: string
    enable_dashboard: boolean
    enable_analytics: boolean
    enable_process_flow: boolean
    enable_batch_history: boolean
    enable_stations: boolean
    enable_jobs: boolean
    enable_labor_tracking: boolean
    enable_product_families: boolean
    enable_operators: boolean
    created_at?: string
    updated_at?: string
}

export interface OrganizationPrefixes {
    organization_id: string
    batch_prefix: string
    created_at?: string
    updated_at?: string
}

export interface UserUsage {
    user_id: string
    email: string
    name: string
    tier: string
    organization_id: string | null
    num_projects: number
    project_ids: number[] | null
    num_features: number
    last_login: string | null
    created_at: string
}

export interface OrganizationMember {
    user_id: string
    email: string
    name: string
    /** Kartorium super admin flag (Kartorium staff only, distinct from client "super user" role) */
    is_super_admin: boolean
    num_projects: number
    num_models: number
    last_login: string | null
    created_at: string
}

export interface OrganizationStats {
    organization_id: string
    num_members: number
    num_projects: number
    num_models: number
    num_asset_types: number
    num_oee_sites: number
    last_activity: string | null
}

export interface SubscriptionItem {
    id: string
    product_id: string
    product_name: string
    quantity: number
    unit_amount: number | null
    interval: string | null
}

export interface OrganizationSubscription {
    organization_id: string
    customer_id: string | null
    tier: string | null
    status: string | null
    interval: string | null
    current_period_start: number | null
    current_period_end: number | null
    cancel_at_period_end: boolean
    items: SubscriptionItem[]
    trial_end: number | null
    created: number | null
}

export interface OrganizationWithStats extends OrganizationListItem {
    stats?: OrganizationStats
    members?: OrganizationMember[]
    subscription?: OrganizationSubscription
}

export interface IndividualSubscriber {
    user_id: string
    email: string
    name: string
    customer_id: string | null
    subscription: OrganizationSubscription | null
    created_at: string
    last_login: string | null
}

export interface UsageSummary {
    total_users: number
    free_tier_users: number
    pro_tier_users: number
    total_projects: number
    public_projects: number
    total_features: number
    active_users_30d: number
    total_models: number
    total_asset_types: number
    total_oee_sites: number
    total_oee_equipment: number
}

export interface ProductAnalyticsSummary {
    active_users: number | string
    active_organizations: number | string
    total_sessions: number | string
    average_session_seconds: number | string | null
    median_session_seconds: number | string | null
    page_views: number | string
    last_seen_at: string | null
}

export interface ProductAnalyticsSession {
    session_id: string
    user_id: string
    email: string | null
    name: string | null
    organization_id: string | null
    started_at: string
    last_seen_at: string
    ended_at: string | null
    duration_seconds: number | string | null
    entry_route: string | null
    exit_route: string | null
    environment: string | null
    page_views: number | string
    event_count: number | string
}

export interface ProjectMember {
    user_id: string
    name: string
    email: string
    permission: string
}

export interface ProjectAdmin {
    project_id: number
    project_name: string
    project_description: string | null
    organization_id: string | null
    is_public: boolean
    shared_with_organization: boolean
    owner_user_id: string | null
    owner_email: string | null
    owner_name: string | null
    num_scenes: number
    num_features: number
    num_members: number
    members: ProjectMember[]
    created_at: string
    last_edited: string | null
}

export interface ModelMember {
    user_id: string
    name: string
    email: string
    permission: string
}

export interface ModelAdmin {
    model_id: number
    model_name: string
    model_description: string | null
    organization_id: string | null
    shared_with_organization: boolean
    state: string
    num_files: number
    num_users: number
    owner_user_id: string | null
    owner_email: string | null
    owner_name: string | null
    users: ModelMember[]
    last_changed: string | null
}

export interface OEESiteAdmin {
    site_id: string
    site_name: string
    site_code: string
    organization_id: string
    is_active: boolean
    num_systems: number
    num_equipment: number
    active_batches: number
    downtime_events_today: number
    incidents_today: number
}

export interface AssetTypeAdmin {
    asset_type_id: number
    asset_type_name: string
    asset_type_description: string | null
    organization_id: string | null
    num_assets: number
    num_fields: number
    owner_user_id: string | null
    owner_email: string | null
    owner_name: string | null
}

export interface AdminFileStats {
    organization_id: string
    file_count: number
    folder_count: number
    total_size: number
}

export interface Auth0Connection {
    id: string
    name: string
    strategy?: string
}

export interface CreateOrgWizardPayload {
    orgDisplayName: string
    orgSlug: string
    organizationLogoUrl?: string
    connectionIds: string[]
    members: Array<{ email: string }>
    spaClientId: string
    stripe: {
        customerName: string
        customerEmail: string
        customerPhone?: string
        customerDescription?: string
        websiteUrl?: string
        addressLine1?: string
        addressLine2?: string
        addressCity?: string
        addressState?: string
        addressPostalCode?: string
        addressCountry?: string
    }
}

export interface CreateOrgWizardResult {
    success: boolean
    auth0OrgId?: string
    auth0OrgSlug?: string
    stripeCustomerId?: string
    connectionsEnabled: number
    membersInvited: number
    errors: Array<{ step: string; message: string }>
}

export interface UsageMetricsResponse {
    users: UserUsage[]
    summary: UsageSummary
}

export interface ProjectScene {
    id: number
    name: string
    type: string
    features: Array<{ id: number; name: string; type: string }>
}

export interface ProjectBreakdown {
    id: number
    name: string
    description: string | null
    is_public: boolean
    owner_email: string
    owner_name: string
    created_at: string
    scenes: ProjectScene[]
}

export interface ApiKeyAdmin {
    id: string
    organization_id: string
    label: string
    key_prefix: string | null
    created_by_user_id: string
    recipient_user_id: string | null
    recipient_email?: string | null
    recipient_name?: string | null
    status: 'pending' | 'active' | 'revoked'
    created_at: string
    revoked_at: string | null
    revoked_by_user_id: string | null
    last_used_at: string | null
    expires_at: string | null
    delivery_sent_at: string | null
    delivery_expires_at: string | null
    revealed_at: string | null
    scopes: string[]
}

export interface CreateApiKeyDeliveryRequest {
    organizationID: string
    label: string
    recipientUserID: string
    expiresAt: string
    scopes: string[]
}

export interface ApiKeyScopeDefinition {
    id: string
    category: string
    action: 'read' | 'write'
    label: string
    description: string
}

export interface CreatedApiKeyAdmin {
    apiKey: ApiKeyAdmin
    deliveryUrl: string
    emailSent: boolean
    emailError?: string | null
}

export interface RevealedApiKeyAdmin {
    key: string
    apiKey: ApiKeyAdmin
}

export interface ApiKeyDeliveryStatus {
    code: string
    message: string
    apiKey?: ApiKeyAdmin
    currentUser?: {
        organizationID: string | null
        userID: string | null
    }
}

@Injectable({
    providedIn: 'root'
})
export class AdminService {
    private _currentOrgSettings = new BehaviorSubject<OrganizationSettings | null>(null)
    public currentOrgSettings$ = this._currentOrgSettings.asObservable()

    private _currentOeePageFlags = new BehaviorSubject<OeePageFlags | null>(null)
    public currentOeePageFlags$ = this._currentOeePageFlags.asObservable()

    get currentOrgSettings(): OrganizationSettings | null {
        return this._currentOrgSettings.getValue()
    }

    get currentOeePageFlags(): OeePageFlags | null {
        return this._currentOeePageFlags.getValue()
    }

    constructor(private _requestService: RequestService) {}

    /**
     * Get all organizations (Kartorium super admin only)
     */
    getAllOrganizations(): Observable<OrganizationListItem[]> {
        const url = `${environment.api}/admin/organizations`
        const options = { error: { operation: 'Get All Organizations' } } as EndpointOptions

        return this._requestService.get<OrganizationListItem[]>(url, options)
    }

    /**
     * Get settings for a specific organization (Kartorium super admin only)
     */
    getOrganizationSettings(orgId: string): Observable<OrganizationSettings> {
        const url = `${environment.api}/admin/organizations/${orgId}/settings`
        const options = { error: { operation: 'Get Organization Settings' } } as EndpointOptions

        return this._requestService.get<OrganizationSettings>(url, options)
    }

    /**
     * Get Auth0 tenant connections for the admin create-org wizard
     */
    getAuth0Connections(): Observable<Auth0Connection[]> {
        const url = `${environment.api}/admin/auth0/connections`
        const options = { error: { operation: 'Get Auth0 Connections' } } as EndpointOptions

        return this._requestService.get<Auth0Connection[]>(url, options)
    }

    /**
     * Execute the admin create-org wizard flow
     */
    executeCreateOrgWizard(payload: CreateOrgWizardPayload): Observable<CreateOrgWizardResult> {
        const url = `${environment.api}/admin/organizations/wizard`
        const options = { error: { operation: 'Execute Create Org Wizard' } } as EndpointOptions

        return this._requestService.create<CreateOrgWizardResult>(url, payload, options)
    }

    /**
     * Get organization stats for admin dashboard (Kartorium super admin only)
     */
    getOrganizationStats(): Observable<OrganizationStats[]> {
        const url = `${environment.api}/admin/organizations/stats`
        const options = { error: { operation: 'Get Organization Stats' } } as EndpointOptions

        return this._requestService.get<OrganizationStats[]>(url, options)
    }

    /**
     * Get members of a specific organization (Kartorium super admin only)
     */
    getOrganizationMembers(orgId: string): Observable<OrganizationMember[]> {
        const url = `${environment.api}/admin/organizations/${orgId}/members`
        const options = { error: { operation: 'Get Organization Members' } } as EndpointOptions

        return this._requestService.get<OrganizationMember[]>(url, options)
    }

    /**
     * Get subscription details for a specific organization (Kartorium super admin only)
     */
    getOrganizationSubscription(orgId: string): Observable<OrganizationSubscription> {
        const url = `${environment.api}/admin/organizations/${orgId}/subscription`
        const options = { error: { operation: 'Get Organization Subscription' } } as EndpointOptions

        return this._requestService.get<OrganizationSubscription>(url, options)
    }

    /**
     * Get subscription details for all organizations (Kartorium super admin only)
     */
    getAllSubscriptions(): Observable<OrganizationSubscription[]> {
        const url = `${environment.api}/admin/subscriptions`
        const options = { error: { operation: 'Get All Subscriptions' } } as EndpointOptions

        return this._requestService.get<OrganizationSubscription[]>(url, options)
    }

    /**
     * Get individual subscribers (users not in organizations but with Stripe subscriptions)
     */
    getIndividualSubscribers(): Observable<IndividualSubscriber[]> {
        const url = `${environment.api}/admin/individuals`
        const options = { error: { operation: 'Get Individual Subscribers' } } as EndpointOptions

        return this._requestService.get<IndividualSubscriber[]>(url, options)
    }

    /**
     * Update settings for a specific organization (Kartorium super admin only)
     */
    updateOrganizationSettings(orgId: string, settings: Partial<OrganizationSettings>): Observable<OrganizationSettings> {
        const url = `${environment.api}/admin/organizations/${orgId}/settings`
        const options = { error: { operation: 'Update Organization Settings' } } as EndpointOptions

        return this._requestService.update<OrganizationSettings>(url, settings, options)
    }

    /**
     * Get settings for current user's organization
     * Used by sidebar and route guards
     * Returns cached settings if available, otherwise fetches from API
     */
    getCurrentOrgSettings(forceRefresh = false): Observable<OrganizationSettings> {
        // Return cached settings if available and not forcing refresh
        if (!forceRefresh && this.currentOrgSettings) {
            return of(this.currentOrgSettings)
        }

        const url = `${environment.api}/organization/settings`
        const options = { error: { operation: 'Get Current Organization Settings', toast: false } } as EndpointOptions

        return this._requestService.get<OrganizationSettings>(url, options).pipe(
            tap(settings => this._currentOrgSettings.next(settings)),
            catchError(() => {
                // Return default settings on error (no modules hidden)
                return of({ organization_id: '', hidden_modules: [], default_home_page: '/projects' })
            })
        )
    }

    /**
     * Check if a module is hidden for the current organization
     */
    isModuleHidden(module: string): boolean {
        const settings = this.currentOrgSettings
        return settings?.hidden_modules?.includes(module) ?? false
    }

    /**
     * Get the default home page for the current organization
     */
    getDefaultHomePage(): string {
        return this.currentOrgSettings?.default_home_page ?? '/projects'
    }

    // ============================================
    // OEE PAGE FLAGS
    // ============================================

    /**
     * Get OEE page flags for a specific organization (Kartorium super admin only)
     */
    getOeePageFlags(orgId: string): Observable<OeePageFlags> {
        const url = `${environment.api}/admin/organizations/${orgId}/feature-flags`
        const options = { error: { operation: 'Get Organization Feature Flags' } } as EndpointOptions

        return this._requestService.get<OeePageFlags>(url, options)
    }

    /**
     * Update OEE page flags for a specific organization (Kartorium super admin only)
     */
    updateOeePageFlags(orgId: string, flags: Partial<OeePageFlags>): Observable<OeePageFlags> {
        const url = `${environment.api}/admin/organizations/${orgId}/feature-flags`
        const options = { error: { operation: 'Update Organization Feature Flags' } } as EndpointOptions

        return this._requestService.update<OeePageFlags>(url, flags, options)
    }

    /**
     * Get OEE page flags for current user's organization
     */
    getCurrentOeePageFlags(forceRefresh = false): Observable<OeePageFlags> {
        if (!forceRefresh && this.currentOeePageFlags) {
            return of(this.currentOeePageFlags)
        }

        const url = `${environment.api}/organization/feature-flags`
        const options = { error: { operation: 'Get Current Organization Feature Flags', toast: false } } as EndpointOptions

        return this._requestService.get<OeePageFlags>(url, options).pipe(
            tap(flags => this._currentOeePageFlags.next(flags)),
            catchError(() => {
                const fallbackFlags: OeePageFlags = {
                    organization_id: '',
                    enable_dashboard: true,
                    enable_analytics: true,
                    enable_process_flow: true,
                    enable_batch_history: true,
                    enable_stations: true,
                    enable_jobs: true,
                    enable_labor_tracking: true,
                    enable_product_families: true,
                    enable_operators: true
                }
                this._currentOeePageFlags.next(fallbackFlags)
                return of(fallbackFlags)
            })
        )
    }

    /**
     * Check if a specific OEE page is enabled for the current org
     */
    isOeePageEnabled(feature: keyof Pick<OeePageFlags, 'enable_dashboard' | 'enable_analytics' | 'enable_process_flow' | 'enable_batch_history' | 'enable_stations' | 'enable_jobs' | 'enable_labor_tracking' | 'enable_product_families' | 'enable_operators'>): boolean {
        return this.currentOeePageFlags?.[feature] ?? true
    }

    // ============================================
    // PREFIXES (Job/Batch Number Customization)
    // ============================================

    /**
     * Get prefixes for a specific organization (Kartorium super admin only)
     */
    getOrganizationPrefixes(orgId: string): Observable<OrganizationPrefixes> {
        const url = `${environment.api}/admin/organizations/${orgId}/prefixes`
        const options = { error: { operation: 'Get Organization Prefixes' } } as EndpointOptions

        return this._requestService.get<OrganizationPrefixes>(url, options)
    }

    /**
     * Update prefixes for a specific organization (Kartorium super admin only)
     */
    updateOrganizationPrefixes(orgId: string, prefixes: Partial<OrganizationPrefixes>): Observable<OrganizationPrefixes> {
        const url = `${environment.api}/admin/organizations/${orgId}/prefixes`
        const options = { error: { operation: 'Update Organization Prefixes' } } as EndpointOptions

        return this._requestService.update<OrganizationPrefixes>(url, prefixes, options)
    }

    /**
     * Get prefixes for current user's organization
     */
    getCurrentOrgPrefixes(): Observable<OrganizationPrefixes> {
        const url = `${environment.api}/organization/prefixes`
        const options = { error: { operation: 'Get Current Organization Prefixes' } } as EndpointOptions

        return this._requestService.get<OrganizationPrefixes>(url, options)
    }

    /**
     * Update prefixes for current user's organization (org admin only)
     */
    updateCurrentOrgPrefixes(prefixes: Partial<OrganizationPrefixes>): Observable<OrganizationPrefixes> {
        const url = `${environment.api}/organization/prefixes`
        const options = { error: { operation: 'Update Organization Prefixes' } } as EndpointOptions

        return this._requestService.update<OrganizationPrefixes>(url, prefixes, options)
    }

    // ============================================
    // USAGE METRICS (Kartorium Super Admin Only)
    // ============================================

    /**
     * Get usage metrics for all users (Kartorium super admin only)
     */
    getUsageMetrics(): Observable<UsageMetricsResponse> {
        const url = `${environment.api}/admin/usage`
        const options = { error: { operation: 'Get Usage Metrics' } } as EndpointOptions

        return this._requestService.get<UsageMetricsResponse>(url, options)
    }

    /**
     * Get first-party product analytics summary for active users and sessions.
     */
    getProductAnalyticsSummary(days = 30): Observable<ProductAnalyticsSummary> {
        const url = `${environment.api}/admin/product-analytics/summary?days=${days}`
        const options = { error: { operation: 'Get Product Analytics Summary' } } as EndpointOptions

        return this._requestService.get<ProductAnalyticsSummary>(url, options)
    }

    /**
     * Get recent first-party product analytics sessions.
     */
    getProductAnalyticsRecentSessions(limit = 50): Observable<ProductAnalyticsSession[]> {
        const url = `${environment.api}/admin/product-analytics/sessions?limit=${limit}`
        const options = { error: { operation: 'Get Product Analytics Sessions' } } as EndpointOptions

        return this._requestService.get<ProductAnalyticsSession[]>(url, options)
    }

    /**
     * Get project breakdown (Kartorium super admin only)
     */
    getProjectBreakdown(projectId: number): Observable<ProjectBreakdown> {
        const url = `${environment.api}/admin/projects/${projectId}`
        const options = { error: { operation: 'Get Project Breakdown' } } as EndpointOptions

        return this._requestService.get<ProjectBreakdown>(url, options)
    }

    /**
     * Get all projects for admin dashboard (Kartorium super admin only)
     */
    getAllProjects(): Observable<ProjectAdmin[]> {
        const url = `${environment.api}/admin/projects`
        const options = { error: { operation: 'Get All Projects' } } as EndpointOptions

        return this._requestService.get<ProjectAdmin[]>(url, options)
    }

    /**
     * Get all models for admin dashboard (Kartorium super admin only)
     */
    getAllModels(): Observable<ModelAdmin[]> {
        const url = `${environment.api}/admin/models`
        const options = { error: { operation: 'Get All Models' } } as EndpointOptions

        return this._requestService.get<ModelAdmin[]>(url, options)
    }

    /**
     * Get OEE summary for admin dashboard (Kartorium super admin only)
     */
    getOEESummary(): Observable<OEESiteAdmin[]> {
        const url = `${environment.api}/admin/oee`
        const options = { error: { operation: 'Get OEE Summary' } } as EndpointOptions

        return this._requestService.get<OEESiteAdmin[]>(url, options)
    }

    /**
     * Get all asset types for admin dashboard (Kartorium super admin only)
     */
    getAllAssetTypes(): Observable<AssetTypeAdmin[]> {
        const url = `${environment.api}/admin/assets`
        const options = { error: { operation: 'Get All Asset Types' } } as EndpointOptions

        return this._requestService.get<AssetTypeAdmin[]>(url, options)
    }

    /**
     * Get admin files overview across all organizations (Kartorium super admin only)
     */
    getAdminFilesOverview(): Observable<AdminFileStats[]> {
        const url = `${environment.api}/admin/files/overview`
        const options = { error: { operation: 'Get Admin Files Overview' } } as EndpointOptions

        return this._requestService.get<AdminFileStats[]>(url, options)
    }

    /** List Kartorium roles for an org (lazy-seeds defaults on first call). Super admin only. */
    listOrgKartoriumRoles(orgId: string): Observable<KartoriumRole[]> {
        const url = `${environment.api}/admin/organizations/${orgId}/roles`
        const options = { error: { operation: 'List Kartorium Roles' } } as EndpointOptions
        return this._requestService.get<KartoriumRole[]>(url, options)
    }

    createOrgKartoriumRole(orgId: string, patch: KartoriumRolePatch & { name: string }): Observable<KartoriumRole> {
        const url = `${environment.api}/admin/organizations/${orgId}/roles`
        const options = { error: { operation: 'Create Kartorium Role' } } as EndpointOptions
        return this._requestService.create<KartoriumRole>(url, patch, options)
    }

    updateOrgKartoriumRole(orgId: string, roleId: string, patch: KartoriumRolePatch): Observable<KartoriumRole> {
        const url = `${environment.api}/admin/organizations/${orgId}/roles/${roleId}`
        const options = { error: { operation: 'Update Kartorium Role' } } as EndpointOptions
        return this._requestService.update<KartoriumRole>(url, patch, options)
    }

    deleteOrgKartoriumRole(orgId: string, roleId: string): Observable<unknown> {
        const url = `${environment.api}/admin/organizations/${orgId}/roles/${roleId}`
        const options = { error: { operation: 'Delete Kartorium Role' } } as EndpointOptions
        return this._requestService.delete<unknown>(url, options)
    }

    listOrgRoleAssignments(orgId: string): Observable<RoleAssignment[]> {
        const url = `${environment.api}/admin/organizations/${orgId}/role-assignments`
        const options = { error: { operation: 'List Role Assignments' } } as EndpointOptions
        return this._requestService.get<RoleAssignment[]>(url, options)
    }

    assignKartoriumRole(orgId: string, userId: string, roleId: string): Observable<unknown> {
        const url = `${environment.api}/admin/organizations/${orgId}/users/${encodeURIComponent(userId)}/roles/${roleId}`
        const options = { error: { operation: 'Assign Kartorium Role' } } as EndpointOptions
        return this._requestService.create<unknown>(url, {}, options)
    }

    unassignKartoriumRole(orgId: string, userId: string, roleId: string): Observable<unknown> {
        const url = `${environment.api}/admin/organizations/${orgId}/users/${encodeURIComponent(userId)}/roles/${roleId}`
        const options = { error: { operation: 'Unassign Kartorium Role' } } as EndpointOptions
        return this._requestService.delete<unknown>(url, options)
    }

    listApiKeys(organizationID: string): Observable<ApiKeyAdmin[]> {
        const url = `${environment.api}/api-keys`
        const params = new HttpParams().set('organization_id', organizationID)
        const options = { error: { operation: 'List API Keys' } } as EndpointOptions
        options.httpOptions = { params }

        return this._requestService.get<ApiKeyAdmin[]>(url, options)
    }

    createApiKey(payload: CreateApiKeyDeliveryRequest): Observable<CreatedApiKeyAdmin> {
        const url = `${environment.api}/api-keys`
        const options = { error: { operation: 'Create API Key' } } as EndpointOptions
        return this._requestService.create<CreatedApiKeyAdmin>(url, payload, options)
    }

    listApiKeyScopes(): Observable<ApiKeyScopeDefinition[]> {
        const url = `${environment.api}/api-keys/scopes`
        const options = { error: { operation: 'List API Key Scopes' } } as EndpointOptions
        return this._requestService.get<ApiKeyScopeDefinition[]>(url, options)
    }

    revokeApiKey(keyId: string, organizationID?: string): Observable<ApiKeyAdmin> {
        const url = `${environment.api}/api-keys/${keyId}`
        const options = { error: { operation: 'Revoke API Key' } } as EndpointOptions
        if (organizationID) {
            options.httpOptions = { params: new HttpParams().set('organization_id', organizationID) }
        }

        return this._requestService.delete<ApiKeyAdmin>(url, options)
    }

    revealApiKey(keyId: string, token: string): Observable<RevealedApiKeyAdmin> {
        const url = `${environment.api}/api-keys/${keyId}/reveal`
        const options = { error: { operation: 'Reveal API Key' } } as EndpointOptions
        return this._requestService.create<RevealedApiKeyAdmin>(url, { token }, options)
    }

    getApiKeyDeliveryStatus(keyId: string, token: string): Observable<ApiKeyDeliveryStatus> {
        const url = `${environment.api}/api-keys/${keyId}/delivery`
        const options = {
            error: { operation: 'Get API Key Delivery Status' },
            httpOptions: { params: new HttpParams().set('token', token) },
        } as EndpointOptions

        return this._requestService.get<ApiKeyDeliveryStatus>(url, options)
    }
}
